/**
 * Shared dependencies
 */
import type { List, ListFormProps } from '../../shared/types/lists';

/**
 * Internal dependencies
 */
export type EditUserListParams = {
	listId: string;
	formData: ListFormProps;
};

/**
 * Update a user's list info
 * @link https://evermade.stoplight.io/docs/valio-app/25ih0d66wj8i9-patch-users-user-id-lists-list-id
 * @param {EditUserListParams} params - The parameters for editing the user's list.
 * @param {string} params.listId - The ID of the list to edit.
 * @param {ListFormProps} params.formData - The form data for the list.
 * @param {string} params.formData.label - The new label for the list.
 * @param {string} params.formData.description - The new description for the list.
 * @return {Promise<List>} A promise that resolves to the updated user's list.
 */
export async function editUserList(params: EditUserListParams): Promise<List> {
	const { host } = window.valioConfig;
	const { session } = window.valioApp;

	// Validate required params

	if (!params.listId) {
		throw new Error('List ID is required to edit a user list.');
	}
	if (!params.formData) {
		throw new Error('Form data is required to edit a user list.');
	}

	// Ensure the session is initialized
	if (!session.initialized) {
		await session.init();
	}

	// Ensure the user is authenticated
	if (!session.userId) {
		return Promise.reject(new Error('User is not authenticated. Cannot edit list.'));
	}

	const headers = await session.authHeader();

	// Make the API request to create the list
	return fetch(`${host}/api/v1/users/${session.userId}/lists/${params.listId}`, {
		method: 'PATCH',
		headers,
		body: JSON.stringify({
			label: params.formData.label,
			description: params.formData.description,
		}),
	})
		.then(async (response) => {
			if (!response.ok) {
				// Handle HTTP errors like 401, 404, 500, etc.
				const errorText = await response.text();
				throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
			}

			const data: List = await response.json();

			return data;
		})
		.catch((error) => {
			console.error('Error updating user list:', error);
			throw error;
		});
}
